import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setIsLoggedInResolver } from '@/lib/api';

const AuthContext = createContext(null);

/**
 * Normalize the user object so admins are treated as Premium across the entire
 * frontend. Admins should always have full access to all paywalled content;
 * this avoids having to sprinkle `(user?.is_premium || user?.is_admin)` checks
 * in every page/component.
 */
function withAdminBypass(u) {
  if (!u) return u;
  if (u.is_admin && !u.is_premium) {
    return { ...u, is_premium: true };
  }
  return u;
}

export function AuthProvider({ children }) {
  const [user, _setUser] = useState(null);
  const setUser = (value) => {
    if (typeof value === 'function') {
      _setUser((prev) => withAdminBypass(value(prev)));
    } else {
      _setUser(withAdminBypass(value));
    }
  };
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Let the axios interceptor know whether to redirect on 401.
  useEffect(() => {
    setIsLoggedInResolver(() => !!user);
  }, [user]);

  // One-time migration: nuke any legacy localStorage token from the
  // previous JWT-in-localStorage era. Cookie is now the source of truth.
  useEffect(() => {
    try {
      localStorage.removeItem('token');
    } catch (e) { /* ignore */ }
  }, []);

  // Fetch unread count function that can be called from anywhere
  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get('/messages/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // Silent: anonymous or transient failures shouldn't surface.
    }
  }, [user]);

  const [im, setIm] = useState({ count: 0, latest_at: null, promo_enabled: false, decided: false });
  const refreshIm = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/im/unread-count');
      setIm(data);
    } catch (error) { /* silent */ }
  }, [user]);

  // On mount, ask the backend who we are (cookie is sent automatically).
  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        // No session — stay logged out.
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  // Whenever user becomes available, kick off unread polling.
  useEffect(() => {
    if (user) {
      refreshUnreadCount();
      refreshIm();
      const interval = setInterval(() => { refreshUnreadCount(); refreshIm(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, refreshUnreadCount, refreshIm]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    setUser(response.data.user);
    // Award daily login points
    try {
      await api.post('/points/earn/daily_login');
    } catch (e) { /* ignore */ }
    return response.data.user;
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) { /* even if the request fails, clear local state */ }
    setUser(null);
    setUnreadCount(0);
    setIm({ count: 0, latest_at: null, promo_enabled: false, decided: false });
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const refresh = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (e) { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, logout, updateUser, refresh,
      unreadCount, refreshUnreadCount, im, refreshIm
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
